/**
 * `<card-flights>`: the layer over the felt that cards cross on their way to a
 * seat, or to the discard pile as a finished round is counted.
 *
 * It only moves cards about. What a card means is the table's business, so
 * every flight reports back once it lands - or once it is called off, when
 * the table it was headed for is gone.
 */

import type { Card } from "@ryzun/haf-saloon-game-engine";
import { setAttribute } from "../dom.js";
import { type Point, FLIGHT_MS } from "../flights.js";
import { BaseElement } from "./base-element.js";
import styles from "./card-flights.css";
import "./playing-card.js";

/** How large an emphasized card swells halfway across. */
const EMPHASIS_SCALE = 1.8;

/** Where a card starts and stops, measured from the layer's own corner. */
export interface FlightPath {
    readonly from: Point;
    readonly to: Point;
    readonly fromTurn: number;
    readonly toTurn: number;
    /** The card's size on landing, against a full-size card. */
    readonly scale: number;
    /** The face to show on the way; a card back when left out. */
    readonly card?: Card;
    /** How long the card is in the air; a deal's pace when left out. */
    readonly durationMs?: number;
    /** Swells and glows on the way, for a card the table should not miss. */
    readonly emphasis?: boolean;
}

function toTransform(point: Point, turn: number, scale: number): string {
    return `translate(${point.x}px, ${point.y}px) rotate(${turn}deg) scale(${scale})`;
}

/** The keyframes for one flight: straight across, or by way of a swell for an emphasized card. */
function getFrames(path: FlightPath): Keyframe[] {
    const start = { transform: toTransform(path.from, path.fromTurn, 1) };
    const end = { transform: toTransform(path.to, path.toTurn, path.scale) };

    if (path.emphasis !== true) {
        return [start, end];
    }

    const middle: Point = { x: (path.from.x + path.to.x) / 2, y: (path.from.y + path.to.y) / 2 };

    return [
        start,
        { transform: toTransform(middle, (path.fromTurn + path.toTurn) / 2, EMPHASIS_SCALE), offset: 0.45 },
        { transform: toTransform(middle, (path.fromTurn + path.toTurn) / 2, EMPHASIS_SCALE), offset: 0.7 },
        end
    ];
}

export class CardFlights extends BaseElement {
    readonly #timers = new Set<ReturnType<typeof setTimeout>>();

    readonly #animations = new Set<Animation>();

    /** Every flight not yet settled, so a cancel can tell each one it never landed. */
    readonly #pending = new Set<(landed: boolean) => void>();

    constructor() {
        super(styles);
    }

    /**
     * Sends one card after the delay, measured at the moment it leaves - a hand
     * grows as its cards arrive, so the path is only known then. Resolves true
     * once the card lands, straight away if there is nowhere to fly it, and
     * false if it is called off first.
     */
    fly(delayMs: number, measure: () => FlightPath | null): Promise<boolean> {
        return new Promise((resolve) => {
            const settle = (landed: boolean): void => {
                if (this.#pending.delete(settle)) {
                    resolve(landed);
                }
            };

            this.#pending.add(settle);

            const timer = setTimeout(() => {
                this.#timers.delete(timer);
                this.#launch(measure(), settle);
            }, delayMs);

            this.#timers.add(timer);
        });
    }

    /** Calls off every card still waiting or in the air. */
    cancel(): void {
        for (const timer of this.#timers) {
            clearTimeout(timer);
        }

        this.#timers.clear();

        for (const settle of [...this.#pending]) {
            settle(false);
        }

        for (const animation of [...this.#animations]) {
            animation.cancel();
        }
    }

    protected render(): void {
        setAttribute(this, "aria-hidden", "true");
    }

    #launch(path: FlightPath | null, settle: (landed: boolean) => void): void {
        if (path === null) {
            settle(true);
            return;
        }

        const card = document.createElement("playing-card");

        card.card = path.card ?? null;
        card.classList.toggle("emphasis", path.emphasis === true);
        this.root.append(card);

        const animation = card.animate(getFrames(path), {
            duration: path.durationMs ?? FLIGHT_MS,
            easing: "cubic-bezier(0.25, 0.8, 0.35, 1)",
            fill: "forwards"
        });

        this.#animations.add(animation);

        // A cancelled animation rejects; the cancel has already settled it.
        animation.finished.then(() => settle(true), () => undefined).finally(() => {
            this.#animations.delete(animation);
            card.remove();
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "card-flights": CardFlights;
    }
}

customElements.define("card-flights", CardFlights);
